import type { GraphNode, NodeDefBase, PortType } from './nodeDefs.js';
import { RUNTIME_VALUE_TYPE_OPTIONS, runtimeValuePortType } from './runtimeNodeTypes.js';

type QueryDef = NodeDefBase & { graphs: ['query'] };
const collection: PortType = 'collection';
const scalarTypes = [
  { value: 'float', label: 'Number' },
  { value: 'string', label: 'String' },
  { value: 'bool', label: 'Boolean' },
] as const;
const outputType = (node: GraphNode): PortType => (node.data.inputType as PortType | undefined) ?? 'collection';
const valueType = (node: GraphNode): PortType => runtimeValuePortType(node.data.valueType);

export const QUERY_NODE_DEFS: Record<string, QueryDef> = {
  State: {
    label: 'State', cat: 'source', graphs: ['query'], defaults: { name: '', scope: 'global', valueType: 'number' }, inputs: [],
    outputs: node => [{ name: 'value', label: 'Value', type: valueType(node) }],
    ui: [
      { kind: 'text', field: 'name', label: 'State', placeholder: 'State name' },
      { kind: 'select', field: 'scope', label: 'Scope', default: 'global', options: [{ value: 'global', label: 'Global' }, { value: 'owner', label: 'Owner' }] },
      { kind: 'select', field: 'valueType', label: 'Type', default: 'number', options: RUNTIME_VALUE_TYPE_OPTIONS },
      { kind: 'port', ref: 'value', io: 'out' },
    ],
  },
  Input: {
    label: 'Input', cat: 'source', graphs: ['query'], defaults: { name: 'input', valueType: 'number', default: 0 }, inputs: [],
    outputs: node => [{ name: 'value', label: 'Value', type: valueType(node) }],
    ui: [{ kind: 'text', field: 'name', label: 'Input' }, { kind: 'select', field: 'valueType', label: 'Type', default: 'number', options: RUNTIME_VALUE_TYPE_OPTIONS }, { kind: 'text', field: 'default', label: 'Default', valueTypeField: 'valueType' }, { kind: 'port', ref: 'value', io: 'out' }],
  },
  Constant: {
    label: 'Constant', cat: 'source', graphs: ['query'], defaults: { valueType: 'number', value: 0 }, inputs: [],
    outputs: node => [{ name: 'value', label: 'Value', type: valueType(node) }],
    ui: [{ kind: 'select', field: 'valueType', label: 'Type', default: 'number', options: RUNTIME_VALUE_TYPE_OPTIONS }, { kind: 'text', field: 'value', label: 'Value', valueTypeField: 'valueType' }, { kind: 'port', ref: 'value', io: 'out' }],
  },
  Source: {
    label: 'Source', cat: 'source', graphs: ['query'], defaults: { source: '' }, inputs: [],
    outputs: [{ name: 'records', label: 'Records', type: collection }],
    ui: [{ kind: 'data-source', field: 'source', label: 'Source' }, { kind: 'port', ref: 'records', io: 'out' }],
  },
  Filter: {
    label: 'Filter', cat: 'logic', graphs: ['query'], defaults: { field: '', op: 'equal', value: '', valueType: 'string' },
    inputs: node => [{ name: 'records', label: 'Records', type: collection }, { name: 'value', label: 'Value', type: valueType(node), optional: true }], outputs: [{ name: 'records', label: 'Records', type: collection }],
    ui: [
      { kind: 'port', ref: 'records', io: 'in' },
      { kind: 'query-field', field: 'field', label: 'Field' },
      { kind: 'select', field: 'valueType', label: 'Type', default: 'string', options: RUNTIME_VALUE_TYPE_OPTIONS },
      { kind: 'select', field: 'op', label: 'Operator', default: 'equal', options: [
        { value: 'equal', label: 'Equal' }, { value: 'notEqual', label: 'Not equal' },
        { value: 'greater', label: 'Greater' }, { value: 'greaterEqual', label: 'Greater or equal' },
        { value: 'less', label: 'Less' }, { value: 'lessEqual', label: 'Less or equal' },
      ] },
      { kind: 'port', ref: 'value', io: 'in' },
      { kind: 'text', field: 'value', label: 'Fallback', valueTypeField: 'valueType' },
      { kind: 'port', ref: 'records', io: 'out' },
    ],
  },
  Select: {
    label: 'Select Fields', cat: 'special', graphs: ['query'], defaults: { fields: '' },
    inputs: [{ name: 'records', label: 'Records', type: collection }], outputs: [{ name: 'records', label: 'Records', type: collection }],
    ui: [{ kind: 'port', ref: 'records', io: 'in' }, { kind: 'text', field: 'fields', label: 'Fields', placeholder: 'title, body' }, { kind: 'port', ref: 'records', io: 'out' }],
  },
  Sort: {
    label: 'Sort', cat: 'special', graphs: ['query'], defaults: { field: '', descending: false },
    inputs: [{ name: 'records', label: 'Records', type: collection }], outputs: [{ name: 'records', label: 'Records', type: collection }],
    ui: [{ kind: 'port', ref: 'records', io: 'in' }, { kind: 'query-field', field: 'field', label: 'Field' }, { kind: 'checkbox', field: 'descending', label: 'Descending', default: false }, { kind: 'port', ref: 'records', io: 'out' }],
  },
  Limit: {
    label: 'Limit', cat: 'special', graphs: ['query'], defaults: { count: 10 },
    inputs: [{ name: 'records', label: 'Records', type: collection }], outputs: [{ name: 'records', label: 'Records', type: collection }],
    ui: [{ kind: 'port', ref: 'records', io: 'in' }, { kind: 'dragger', field: 'count', label: 'Count', min: 0, max: 100000, step: 1 }, { kind: 'port', ref: 'records', io: 'out' }],
  },
  Find: {
    label: 'Find', cat: 'special', graphs: ['query'], defaults: { field: '', value: '', valueType: 'string' },
    inputs: node => [{ name: 'records', label: 'Records', type: collection }, { name: 'value', label: 'Value', type: valueType(node), optional: true }], outputs: [{ name: 'record', label: 'Record', type: 'record' }],
    ui: [{ kind: 'port', ref: 'records', io: 'in' }, { kind: 'query-field', field: 'field', label: 'Field' }, { kind: 'select', field: 'valueType', label: 'Type', default: 'string', options: RUNTIME_VALUE_TYPE_OPTIONS }, { kind: 'port', ref: 'value', io: 'in' }, { kind: 'text', field: 'value', label: 'Fallback', valueTypeField: 'valueType' }, { kind: 'port', ref: 'record', io: 'out' }],
  },
  Field: {
    label: 'Field', cat: 'special', graphs: ['query'], defaults: { field: '', valueType: 'string' },
    inputs: [{ name: 'record', label: 'Record', type: 'record' }], outputs: node => [{ name: 'value', label: 'Value', type: valueType(node) }],
    ui: [{ kind: 'port', ref: 'record', io: 'in' }, { kind: 'query-field', field: 'field', label: 'Field' }, { kind: 'select', field: 'valueType', label: 'Type', default: 'string', options: RUNTIME_VALUE_TYPE_OPTIONS }, { kind: 'port', ref: 'value', io: 'out' }],
  },
  Count: aggregate('Count', false),
  Sum: aggregate('Sum', true),
  Min: aggregate('Minimum', true),
  Max: aggregate('Maximum', true),
  Average: aggregate('Average', true),
  Output: {
    label: 'Output', cat: 'output', graphs: ['query'], defaults: { inputName: 'result', inputType: 'collection' },
    inputs: node => [{ name: 'value', label: String(node.data.inputName || 'Result'), type: outputType(node) }], outputs: [],
    ui: [
      { kind: 'text', field: 'inputName', placeholder: 'Output name' },
      { kind: 'select', field: 'inputType', label: 'Type', default: 'collection', options: [
        { value: 'collection', label: 'Collection' }, { value: 'record', label: 'Record' }, ...scalarTypes,
      ] },
      { kind: 'port', ref: 'value', io: 'in' },
    ],
  },
};

function aggregate(label: string, needsField: boolean): QueryDef {
  return {
    label, cat: 'math', graphs: ['query'], defaults: needsField ? { field: '' } : {},
    inputs: [{ name: 'records', label: 'Records', type: collection }], outputs: [{ name: 'value', label: 'Value', type: 'float' }],
    ui: [
      { kind: 'port', ref: 'records', io: 'in' },
      ...(needsField ? [{ kind: 'query-field' as const, field: 'field', label: 'Field' }] : []),
      { kind: 'port', ref: 'value', io: 'out' },
    ],
  };
}
